import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, platforms, articles } from '@/mocks/data';

const categoryImages: Record<string, string> = {
  'japan-cat': 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20Japanese%20modern%20interior%20with%20warm%20ambient%20lighting%2C%20sleek%20architecture%2C%20subtle%20cultural%20elements%2C%20premium%20atmosphere%2C%20editorial%20photography&width=1200&height=500&seq=cat-hero-japan&orientation=landscape',
  'korea-cat': 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20Korean%20contemporary%20space%20with%20warm%20lighting%2C%20modern%20design%2C%20clean%20aesthetic%2C%20premium%20atmosphere%2C%20editorial%20photography&width=1200&height=500&seq=cat-hero-korea&orientation=landscape',
  'sea-cat': 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20tropical%20resort%20interior%20with%20warm%20sunlight%2C%20natural%20materials%2C%20serene%20atmosphere%2C%20premium%20aesthetic%2C%20editorial%20photography&width=1200&height=500&seq=cat-hero-sea&orientation=landscape',
  'cn-cat': 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20Chinese%20modern%20interior%20with%20warm%20lantern%20lighting%2C%20contemporary%20design%2C%20premium%20atmosphere%2C%20editorial%20photography&width=1200&height=500&seq=cat-hero-cn&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryPlatforms = platforms.filter((p) => p.category === category.slug);
  const categoryArticles = articles.filter((a) => a.category === category.slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 亚洲无码正版内容平台推荐指南`,
    description: category.description,
    url: `https://shakea1.cn/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '亚洲无码正版内容平台推荐指南',
      url: 'https://shakea1.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden" aria-label={`${category.name}分类页Hero`}>
          <img
            src={categoryImages[category.imageSeq] || categoryImages['japan-cat']}
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="text-center">
              <h1 className="font-heading text-xl md:text-3xl font-bold text-white mb-2">
                {category.name}正版内容平台推荐
              </h1>
              <p className="text-sm text-white/80 max-w-lg mx-auto">{category.description}</p>
            </div>
          </div>
        </section>

        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: category.name },
          ]}
        />

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10" aria-labelledby="cat-platforms-title">
          <h2 id="cat-platforms-title" className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-6">
            <a href="#cat-platforms-title">推荐平台</a>
          </h2>

          {categoryPlatforms.length === 0 ? (
            <p className="text-sm text-foreground-500">暂无该分类的平台推荐，敬请期待更新。</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categoryPlatforms.map((platform) => (
                <Link
                  key={platform.id}
                  to={`/article/${platform.slug}`}
                  className="flex items-start gap-4 p-4 md:p-5 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-200/50 transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                    <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                      <i className="ri-building-line"></i>
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {platform.name}
                      </h3>
                      {platform.verified && (
                        <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                          <i className="ri-verified-badge-fill text-xs"></i>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-accent-500 font-medium">{platform.rating}</span>
                      <span className="text-xs text-foreground-500">({platform.reviewCount.toLocaleString()}条评价)</span>
                    </div>
                    <p className="text-xs text-foreground-600 line-clamp-2">{platform.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {categoryArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14" aria-labelledby="cat-articles-title">
            <h2 id="cat-articles-title" className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-6">
              <a href="#cat-articles-title">相关文章</a>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <article key={article.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200/50 transition-all group">
                  <Link to={`/article/${article.slug}`} className="block p-4 md:p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                      <span>{article.publishDate}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2">{article.excerpt}</p>
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}